import React, { useState } from 'react';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Progress } from './ui/progress';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import {
  calculateSavingsGrowth,
  calculateDownPaymentPercentage,
  getDownPaymentStatus,
} from 'utils/mortgage-calculator/savingsCalculations';
import { buildClassName } from 'utils/build-class-name';
import { AnalyticsController } from 'analytics/analytics-controller';
import { GTM_CALC_LEAD_FORM_SUB } from 'constants/events';
import cardStyles from './ui/card.module.scss';
import styles from './FutureHomePlanner.module.scss';

const FutureHomePlanner = () => {
  const [monthlySavings, setMonthlySavings] = useState(1000);
  const [annualReturn, setAnnualReturn] = useState(5);
  const [savingYears, setSavingYears] = useState(5);
  const [dreamHomePrice, setDreamHomePrice] = useState(600000);

  const projection = calculateSavingsGrowth(monthlySavings, annualReturn, savingYears);
  const downPaymentPercentage = calculateDownPaymentPercentage(projection.totalAmount, dreamHomePrice);
  const status = getDownPaymentStatus(downPaymentPercentage);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const progressValue = Math.min((downPaymentPercentage / 20) * 100, 100);

  return (
    <div className={styles.component}>
      <div className={styles.mainGrid}>
        {/* Left Column - Inputs */}
        <Card className={styles.inputCard}>
          <h3 className={styles.cardTitle}>
            <span className={styles.icon}>📈</span>
            Your Savings Plan
          </h3>

          <div className={styles.inputSection}>
            {/* Monthly Savings */}
            <div className={styles.inputGroup}>
              <div className={styles.inputHeader}>
                <Label htmlFor="monthly-savings" className={styles.inputLabel}>
                  Monthly Savings
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <span className={styles.infoIcon}>ℹ️</span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p style={{ maxWidth: '18rem' }}>How much you plan to save each month toward your down payment</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </Label>
                <span className={styles.inputValue}>{formatCurrency(monthlySavings)}</span>
              </div>
              <Slider
                id="monthly-savings"
                min={100}
                max={5000}
                step={50}
                value={[monthlySavings]}
                onValueChange={value => setMonthlySavings(value[0])}
                className={styles.slider}
              />
              <Input
                type="number"
                value={monthlySavings}
                onChange={e => setMonthlySavings(Number(e.target.value))}
                className={styles.input}
              />
            </div>

            {/* Annual Return */}
            <div className={styles.inputGroup}>
              <div className={styles.inputHeader}>
                <Label htmlFor="annual-return" className={styles.inputLabel}>
                  Expected Annual Return
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <span className={styles.infoIcon}>ℹ️</span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p style={{ maxWidth: '18rem' }}>Expected investment return rate (e.g., TFSA, RRSP, GIC). Conservative: 3-5%, Moderate: 5-7%</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </Label>
                <span className={styles.inputValue}>{annualReturn.toFixed(1)}%</span>
              </div>
              <Slider
                id="annual-return"
                min={0}
                max={15}
                step={0.5}
                value={[annualReturn]}
                onValueChange={value => setAnnualReturn(value[0])}
                className={styles.slider}
              />
              <Input
                type="number"
                value={annualReturn}
                onChange={e => setAnnualReturn(Number(e.target.value))}
                step="0.1"
                className={styles.input}
              />
            </div>

            {/* Saving Years */}
            <div className={styles.inputGroup}>
              <div className={styles.inputHeader}>
                <Label htmlFor="saving-years" className={styles.inputLabel}>
                  Saving Timeline
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <span className={styles.infoIcon}>ℹ️</span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p style={{ maxWidth: '18rem' }}>How many years you plan to save before buying</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </Label>
                <span className={styles.inputValue}>{savingYears} years</span>
              </div>
              <Slider
                id="saving-years"
                min={1}
                max={15}
                step={1}
                value={[savingYears]}
                onValueChange={value => setSavingYears(value[0])}
                className={styles.slider}
              />
            </div>

            {/* Dream Home Price */}
            <div className={styles.inputGroup}>
              <div className={styles.inputHeader}>
                <Label htmlFor="dream-home-price" className={styles.inputLabel}>
                  <span className={styles.icon}>🏠</span>
                  Dream Home Price
                </Label>
                <span className={styles.inputValue}>{formatCurrency(dreamHomePrice)}</span>
              </div>
              <Slider
                id="dream-home-price"
                min={200000}
                max={2000000}
                step={10000}
                value={[dreamHomePrice]}
                onValueChange={value => setDreamHomePrice(value[0])}
                className={styles.slider}
              />
              <Input
                type="number"
                value={dreamHomePrice}
                onChange={e => setDreamHomePrice(Number(e.target.value))}
                className={styles.input}
              />
            </div>
          </div>
        </Card>

        {/* Right Column - Projections */}
        <div className={styles.projectionColumn}>
          {/* Down Payment Projection */}
          <Card className={styles.projectionCard}>
            <h3 className={styles.projectionTitle}>Your Projected Down Payment</h3>

            <div className={styles.projectionContent}>
              <div className={styles.projectionAmount}>
                <div className={styles.projectionAmountValue}>
                  {formatCurrency(projection.totalAmount)}
                </div>
                <p className={styles.projectionSubtext}>in {savingYears} years</p>
              </div>

              <div className={styles.projectionStats}>
                <div className={styles.projectionStat}>
                  <div className={styles.projectionStatValue}>{formatCurrency(projection.totalSavings)}</div>
                  <p className={styles.projectionStatLabel}>Total Contributions</p>
                </div>
                <div className={styles.projectionStat}>
                  <div className={buildClassName(styles.projectionStatValue, styles.projectionStatValueAccent)}>{formatCurrency(projection.investmentGrowth)}</div>
                  <p className={styles.projectionStatLabel}>Investment Growth</p>
                </div>
              </div>

              <div className={styles.projectionFooter}>
                <p className={styles.projectionFooterText}>
                  <span className={styles.projectionFooterHighlight}>In {savingYears} years</span>, you could be ready for a home worth{' '}
                  <span className={styles.projectionFooterAccent}>{formatCurrency(dreamHomePrice)}</span>
                </p>
              </div>
            </div>
          </Card>

          {/* Dream Scenario Status */}
          <Card className={styles.progressCard}>
            <h3 className={styles.progressTitle}>Down Payment Goal Progress</h3>

            <div className={styles.progressSection}>
              <div>
                <div className={styles.progressHeader}>
                  <span className={styles.progressLabel}>Down Payment: {downPaymentPercentage.toFixed(1)}%</span>
                  <span className={styles.progressStatus} style={{ color: status.color }}>{status.level}</span>
                </div>
                <Progress value={progressValue} className={styles.progressBar} />
                <p className={styles.progressMessage} style={{ color: status.color }}>{status.message}</p>
              </div>

              <div className={styles.milestonesGrid}>
                <div className={buildClassName(styles.milestone, downPaymentPercentage >= 5 && styles.milestoneActive)}>
                  <div className={styles.milestoneLabel}>5%</div>
                  <div className={styles.milestoneValue}>Minimum</div>
                </div>
                <div className={buildClassName(styles.milestone, downPaymentPercentage >= 10 && styles.milestoneActiveBlue)}>
                  <div className={styles.milestoneLabel}>10%</div>
                  <div className={styles.milestoneValue}>Good</div>
                </div>
                <div className={buildClassName(styles.milestone, downPaymentPercentage >= 20 && styles.milestoneActiveAccent)}>
                  <div className={styles.milestoneLabel}>20%</div>
                  <div className={styles.milestoneValue}>Excellent</div>
                </div>
              </div>

              {downPaymentPercentage >= 20 && (
                <div className={styles.congratulationsCard}>
                  <p className={styles.congratulationsTitle}>🎉 Congratulations!</p>
                  <p className={styles.congratulationsText}>
                    With a 20%+ down payment, you&apos;ll save thousands by avoiding CMHC insurance premiums.
                  </p>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>

      {/* Savings Growth Chart */}
      <Card className={styles.chartCard}>
        <h3 className={styles.chartTitle}>Savings Growth Over Time</h3>
        <div className={styles.chartPlaceholder}>
          <p className={styles.chartPlaceholderText}>Chart visualization (can be enhanced with Victory charts)</p>
        </div>
      </Card>

      {/* Agent Engagement CTA */}
      <Card className={buildClassName(cardStyles.shadowSmooth, styles.ctaCard)}>
        <div className={styles.ctaContent}>
          <div className={styles.ctaIconContainer}>
            <span className={styles.ctaIcon}>💬</span>
          </div>

          <h3 className={styles.ctaTitle}>
            Ready to Explore Your Path to Homeownership?
          </h3>

          <p className={styles.ctaDescription}>
            Chat with a Zoocasa agent to see how close you are — and how to get there faster.
          </p>

          <div className={styles.ctaFeatures}>
            <div className={styles.ctaFeature}>
              <div className={styles.ctaFeatureDot} />
              <div className={styles.ctaFeatureContent}>
                <p className={styles.ctaFeatureTitle}>Validate Your Plan</p>
                <p className={styles.ctaFeatureDescription}>Get expert feedback on whether your savings strategy is realistic and optimal</p>
              </div>
            </div>
            <div className={styles.ctaFeature}>
              <div className={styles.ctaFeatureDot} />
              <div className={styles.ctaFeatureContent}>
                <p className={styles.ctaFeatureTitle}>Discover Neighbourhoods</p>
                <p className={styles.ctaFeatureDescription}>Find areas that match your future budget and lifestyle preferences</p>
              </div>
            </div>
            <div className={styles.ctaFeature}>
              <div className={styles.ctaFeatureDot} />
              <div className={styles.ctaFeatureContent}>
                <p className={styles.ctaFeatureTitle}>Understand Market Trends</p>
                <p className={styles.ctaFeatureDescription}>Learn whether waiting or moving sooner makes more sense for your situation</p>
              </div>
            </div>
            <div className={styles.ctaFeature}>
              <div className={styles.ctaFeatureDot} />
              <div className={styles.ctaFeatureContent}>
                <p className={styles.ctaFeatureTitle}>Explore All Options</p>
                <p className={styles.ctaFeatureDescription}>Discover pre-construction opportunities and long-term planning strategies</p>
              </div>
            </div>
          </div>

          <Button
            size="lg"
            className={styles.ctaButton}
            onClick={() => AnalyticsController.trackEvent(GTM_CALC_LEAD_FORM_SUB, 'Mortgage Calculator')}
          >
            Connect with a Zoocasa Agent
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default FutureHomePlanner;
